import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  Rocket,
  CheckCircle2,
  AlertCircle,
  Info,
  Star,
  Clock,
  ArrowRight,
  ChevronRight,
  Filter,
  Search,
  LayoutGrid,
  List as ListIcon,
  Sparkles
} from 'lucide-react';
import { UPDATES } from '@/data/updates';
import { AppUpdate } from '@/types/updates';
import SEO from '@/components/SEO';
import { BackgroundAnimation } from '@/components/background-animation';
import { DiscordCTA } from '@/components/DiscordCTA';
import InternalLinksSection from '@/components/InternalLinksSection';
import { BookOpen, FileText } from 'lucide-react';

const Updates: React.FC = () => {
  const [filter, setFilter] = useState<'ALL' | 'FEATURE' | 'FIX' | 'IMPROVEMENT'>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const filteredUpdates = UPDATES.filter(u => {
    const matchesCategory = filter === 'ALL' || u.category === filter;
    const matchesSearch = u.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'FEATURE': return <Rocket className="w-4 h-4 text-cyan-600" />;
      case 'FIX': return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
      case 'IMPROVEMENT': return <Star className="w-4 h-4 text-purple-600" />;
      default: return <Info className="w-4 h-4 text-zinc-400" />;
    }
  };

  const getCategoryStyle = (category: string) => {
    switch (category) {
      case 'FEATURE': return 'bg-cyan-50 text-cyan-700 border-cyan-100';
      case 'FIX': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'IMPROVEMENT': return 'bg-purple-50 text-purple-700 border-purple-100';
      default: return 'bg-zinc-50 text-zinc-700 border-zinc-100';
    }
  };

  return (
    <div className="min-h-screen bg-[#ffffff] pt-24 pb-32 overflow-x-hidden font-sans selection:bg-zinc-950 selection:text-white">
      <BackgroundAnimation opacity={0.05} />
      <SEO
        title="App Updates"
        description="The evolution of the Satisfying Ball Simulation engine. Track our progress month by month."
        canonical="/updates"
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <header className="mb-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-50 text-[10px] font-black uppercase tracking-widest text-cyan-600 border border-cyan-100"
              >
                <Sparkles className="w-3 h-3 text-cyan-800" />
                Evolution Ledger
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-4xl md:text-6xl font-black tracking-tighter text-cyan-950 leading-none"
              >
                System <span className="text-cyan-600">Updates</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-base text-zinc-500 max-w-2xl font-medium"
              >
                Comprehensive logs of every feature, fix, and technological leap in the simulation engine since November 2025.
              </motion.p>
            </div>
          </div>
        </header>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-16 p-4 bg-cyan-50/50 backdrop-blur-xl border border-cyan-100 rounded-[2rem]">
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            {(['ALL', 'FEATURE', 'FIX', 'IMPROVEMENT'] as const).map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-6 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-[0.15em] transition-all border ${filter === cat
                  ? 'bg-cyan-600 text-white border-cyan-600 shadow-xl shadow-cyan-600/20'
                  : 'bg-white text-zinc-400 border-cyan-100 hover:border-cyan-300 hover:text-cyan-600'
                  }`}
              >
                {cat === 'ALL' ? 'Everything' : cat + 'S'}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="relative flex-grow md:w-64">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-300" />
              <input
                type="text"
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white border border-cyan-100 rounded-2xl py-3 pl-12 pr-4 text-xs font-bold uppercase tracking-widest focus:outline-none focus:ring-4 focus:ring-cyan-600/10 focus:border-cyan-600 transition-all placeholder:text-zinc-200"
              />
            </div>
            <div className="flex items-center bg-white border border-cyan-100 rounded-2xl p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-cyan-600 text-white shadow-md' : 'text-zinc-300 hover:text-cyan-600'}`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-xl transition-all ${viewMode === 'list' ? 'bg-cyan-600 text-white shadow-md' : 'text-zinc-300 hover:text-cyan-600'}`}
              >
                <ListIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <motion.div
          layout
          className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" : "space-y-6"}
        >
          <AnimatePresence mode="popLayout">
            {filteredUpdates.map((update, idx) => (
              <motion.div
                key={update.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: idx * 0.05 }}
                className="group h-full"
              >
                <Link to={`/updates/${update.slug}`} className="block h-full">
                  <article className={`h-full bg-white border border-cyan-100 rounded-[2.5rem] p-8 shadow-sm hover:shadow-2xl hover:shadow-cyan-600/5 hover:-translate-y-1 transition-all duration-500 relative overflow-hidden flex flex-col`}>
                    {/* Subtle Gradient background on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-cyan-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    <div className="relative z-10 flex flex-col h-full">
                      <div className="flex items-center justify-between gap-4 mb-8">
                        <span className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-[0.2em] border flex items-center gap-2 ${getCategoryStyle(update.category)}`}>
                          {getCategoryIcon(update.category)}
                          {update.category}
                        </span>
                        <div className="flex items-center gap-2 text-[10px] text-cyan-300 font-bold uppercase tracking-widest">
                          <Calendar className="w-3 h-3" />
                          {update.date}
                        </div>
                      </div>

                      <div className="mb-6">
                        <h3 className="text-xl font-black tracking-tight text-cyan-950 group-hover:text-cyan-600 transition-colors leading-tight">
                          {update.title}
                        </h3>
                      </div>

                      <p className="text-sm text-zinc-500 font-medium leading-relaxed mb-8 line-clamp-3">
                        {update.description}
                      </p>

                      <div className="mt-auto pt-8 border-t border-cyan-50 flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest">Build Version</span>
                          <span className="text-xs font-black text-cyan-950">v{update.version || '0.0.0'}</span>
                        </div>
                        <div className="flex items-center gap-2 group/btn cursor-pointer">
                          <span className="text-[10px] font-black text-cyan-950 uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity translate-x-2 group-hover:translate-x-0 transition-transform">Read Log</span>
                          <div className="w-10 h-10 rounded-2xl bg-cyan-50 border border-cyan-100 flex items-center justify-center group-hover:bg-cyan-600 group-hover:text-white transition-all text-cyan-600">
                            <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </article>
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {filteredUpdates.length === 0 && (
          <div className="text-center py-32 bg-zinc-50 rounded-[4rem] border border-dashed border-zinc-200 mx-auto max-w-xl">
            <Rocket className="w-12 h-12 text-zinc-200 mx-auto mb-6" />
            <h3 className="text-xl font-black text-zinc-950 uppercase tracking-tight">No logs matched your query</h3>
            <p className="text-zinc-400 font-medium text-sm mt-3 px-12">The system was unable to locate update logs matching your current filters or search term.</p>
            <button
              onClick={() => { setFilter('ALL'); setSearchTerm(''); }}
              className="mt-8 px-8 py-3 bg-white border border-zinc-200 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-zinc-950 hover:text-white hover:border-zinc-950 transition-all"
            >
              Reset Search Filter
            </button>
          </div>
        )}

        {/* Resource Links Section */}
        <section className="mt-24 pt-16 border-t border-cyan-100 max-w-4xl mx-auto space-y-6">
          <h3 className="text-lg font-bold font-display uppercase tracking-tight text-zinc-950 text-left">Learn More About Fun Circle Games</h3>
          <p className="text-zinc-500 text-sm font-medium font-sans text-left">
            Ready to explore further? Check out our other developer and creator resources:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/plans" className="p-4 rounded-2xl bg-zinc-50 border border-zinc-100 hover:border-cyan-600/30 flex flex-col justify-between group transition-all text-left">
              <div className="space-y-3">
                <div className="w-8 h-8 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center group-hover:bg-cyan-600 group-hover:text-white transition-all">
                  <Sparkles className="w-4 h-4" />
                </div>
                <h4 className="text-xs font-bold text-zinc-900 uppercase">Pricing Plans</h4>
                <p className="text-[10px] text-muted-foreground">Level up your rendering speed & workspace storage.</p>
              </div>
              <div className="mt-4 text-[9px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                Pricing <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link to="/tutorials" className="p-4 rounded-2xl bg-zinc-50 border border-zinc-100 hover:border-cyan-600/30 flex flex-col justify-between group transition-all text-left">
              <div className="space-y-3">
                <div className="w-8 h-8 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center group-hover:bg-cyan-600 group-hover:text-white transition-all">
                  <BookOpen className="w-4 h-4" />
                </div>
                <h4 className="text-xs font-bold text-zinc-900 uppercase">Tutorials</h4>
                <p className="text-[10px] text-muted-foreground">Read step-by-step guides on making satisfying visual loops.</p>
              </div>
              <div className="mt-4 text-[9px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                Tutorials <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link to="/blog" className="p-4 rounded-2xl bg-zinc-50 border border-zinc-100 hover:border-cyan-600/30 flex flex-col justify-between group transition-all text-left">
              <div className="space-y-3">
                <div className="w-8 h-8 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center group-hover:bg-cyan-600 group-hover:text-white transition-all">
                  <FileText className="w-4 h-4" />
                </div>
                <h4 className="text-xs font-bold text-zinc-900 uppercase">Journal</h4>
                <p className="text-[10px] text-muted-foreground">Read about trends, updates, and engine architecture insights.</p>
              </div>
              <div className="mt-4 text-[9px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                Blog <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link to="/showcase" className="p-4 rounded-2xl bg-zinc-50 border border-zinc-100 hover:border-cyan-600/30 flex flex-col justify-between group transition-all text-left">
              <div className="space-y-3">
                <div className="w-8 h-8 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center group-hover:bg-cyan-600 group-hover:text-white transition-all">
                  <Rocket className="w-4 h-4" />
                </div>
                <h4 className="text-xs font-bold text-zinc-900 uppercase">Showcase</h4>
                <p className="text-[10px] text-muted-foreground">View and remix simulations created by the community.</p>
              </div>
              <div className="mt-4 text-[9px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                Showcase <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </section>

        <footer className="mt-32 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="inline-block p-12 bg-white border border-cyan-100 rounded-[3.5rem] relative overflow-hidden w-full max-w-4xl shadow-sm"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-100 blur-[120px] rounded-full -z-0 opacity-60" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-50 blur-[100px] rounded-full -z-0 opacity-40" />
            <div className="relative z-10 space-y-8">
              <h2 className="text-3xl md:text-5xl font-black tracking-tighter uppercase leading-none text-cyan-950">Stay Synchronized</h2>
              <p className="text-zinc-500 font-medium max-w-md mx-auto text-sm md:text-base">
                Join our newsletter to receive architectural updates and early access keys for new experimental builds.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
                <input
                  type="email"
                  placeholder="enter.your@email.com"
                  className="w-full sm:w-80 bg-cyan-50/50 border border-cyan-100 rounded-2xl h-14 px-6 text-xs font-bold uppercase tracking-widest text-cyan-950 focus:outline-none focus:ring-4 focus:ring-cyan-600/10 focus:border-cyan-600 transition-all placeholder:text-cyan-300"
                />
                <button className="w-full sm:w-auto h-14 px-10 bg-cyan-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:scale-105 active:scale-95 transition-all shadow-xl shadow-cyan-600/20">
                  Subscribe
                </button>
              </div>
            </div>
          </motion.div>
        </footer>

        <div className="mt-20">
          <DiscordCTA variant="banner" />
        </div>

        <div className="mt-12">
          <InternalLinksSection />
        </div>
      </div>
    </div>
  );
};

// Simple Calendar icon if not imported from lucide
const Calendar = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /></svg>
);

export default Updates;
